// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ActionMenuItem } from '@/components/action-menu-item';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import {
  AddRounded,
  ArrowDropDownRounded,
  MultipleStopOutlined,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import {
  TimeRecordFilter,
  TimeRecordSortProperty,
  timeRecordSortPropertySchema,
} from '@/costs/api/get-time-records';
import { TimeDataImportDialog } from '@/costs/components/time-data-import-dialog';
import { TimeDataUpdateDialog } from '@/costs/components/time-data-update-dialog';
import { TimeRecordAnalytics } from '@/costs/components/time-record-analytics';
import { TimeRecordDialog } from '@/costs/components/time-record-dialog';
import { TimeRecordFilterBar } from '@/costs/components/time-record-filter-bar';
import { TimeRecordTableSkeleton } from '@/costs/components/time-record-table-skeleton';
import { TimeRecordTableView } from '@/costs/components/time-record-table-view';
import { TimeRecordsSummary } from '@/costs/components/time-records-summary';
import { TimeViewModeToggle } from '@/costs/components/time-view-mode-toggle';
import { useDisclosure } from '@/hooks/use-disclosure';
import { TrackingViewMode, trackingViewModeSchema } from '@/report/types';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Button,
  ButtonGroup,
  Container,
  Menu,
  Stack,
  debounce,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';

const DEFAULT_SORT_PROPERTY: TimeRecordSortProperty = 'incurredOn';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

const timeRecordSearchSchema = z.object({
  sort: timeRecordSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  page: z.number().optional().catch(undefined),
  search: searchTermSchema,
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  people: z.array(z.string()).optional(),
  projects: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
  add: z.boolean().optional().catch(true),
  view: trackingViewModeSchema.optional().catch('table'),
});

export const Route = createFileRoute('/_app/time/')({
  component: TimeRecordsRoute,
  validateSearch: timeRecordSearchSchema,
});

function TimeRecordsRoute() {
  const navigate = Route.useNavigate();
  const searchParameters = Route.useSearch();
  const queryClient = useQueryClient();
  const createTimeRecordDialog = useDisclosure(false);
  const timeDataImportDialog = useDisclosure();
  const timeDataUpdateDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const openCreateTimeRecordDialog = createTimeRecordDialog.open;

  const actionGroupRef = useRef<HTMLDivElement>(null);
  const didOpenCreateTimeRecordDialogFromSearch = useRef(false);
  const { user: currentUser } = useCurrentUser();

  useEffect(() => {
    if (!searchParameters.add) {
      didOpenCreateTimeRecordDialogFromSearch.current = false;
      return;
    }

    if (!currentUser || didOpenCreateTimeRecordDialogFromSearch.current) {
      return;
    }

    didOpenCreateTimeRecordDialogFromSearch.current = true;
    openCreateTimeRecordDialog();
  }, [searchParameters.add, currentUser, openCreateTimeRecordDialog]);

  const updateSearch = useMemo(
    () =>
      debounce((search: string) => {
        // Reset to page 1 when search changes
        const newSearch = omit(searchParameters, 'page');

        if (search) {
          newSearch.search = search;
        } else {
          delete newSearch.search;
        }

        navigate({ search: newSearch });
      }, 300),
    [navigate, searchParameters]
  );

  // The current sort state is constructed by first checking the URL search
  // parameters. If not present, use the default values.
  const sort = {
    property: searchParameters.sort ?? DEFAULT_SORT_PROPERTY,
    direction: searchParameters.direction ?? DEFAULT_SORT_DIRECTION,
  };

  const viewMode: TrackingViewMode = searchParameters.view ?? 'table';

  const timeRecordFilter: TimeRecordFilter = {
    pageSize: 50,
    pageNumber: searchParameters.page ?? 1,
    sort,
    search: searchParameters.search,
    startDate: searchParameters.startDate,
    endDate: searchParameters.endDate,
    personIds: searchParameters.people ?? [],
    projectIds: searchParameters.projects ?? [],
    categories: searchParameters.categories ?? [],
  };

  // Memoize the summary filter to prevent unnecessary re-renders and query key changes
  const summaryFilter = useMemo(
    () => ({
      search: searchParameters.search,
      startDate: searchParameters.startDate,
      endDate: searchParameters.endDate,
      personIds: searchParameters.people,
      projectIds: searchParameters.projects,
      categories: searchParameters.categories,
    }),
    [
      searchParameters.search,
      searchParameters.startDate,
      searchParameters.endDate,
      searchParameters.people,
      searchParameters.projects,
      searchParameters.categories,
    ]
  );

  const setSort = (
    property: TimeRecordSortProperty,
    direction: SortDirection
  ) => {
    const newSearch = { ...searchParameters };

    // Clean up default sort values from URL
    if (property === DEFAULT_SORT_PROPERTY) {
      delete newSearch.sort;
    } else {
      newSearch.sort = property;
    }

    if (direction === DEFAULT_SORT_DIRECTION) {
      delete newSearch.direction;
    } else {
      newSearch.direction = direction;
    }

    // Reset to page 1 when sort changes
    delete newSearch.page;

    navigate({ search: newSearch });
  };

  const handleSort = (property: TimeRecordSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handleChangePage = (pageNumber: number) => {
    const newSearch = { ...searchParameters };

    // Clean up page 1 from URL (it's the default)
    if (pageNumber === 1) {
      delete newSearch.page;
    } else {
      newSearch.page = pageNumber;
    }

    navigate({ search: newSearch });
  };

  const handleSearchChange = (search: string) => {
    updateSearch(search);
  };

  const handleDateRangeChange = (dateRange: DateRange<DateTime>) => {
    const [startDate, endDate] = dateRange;
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (startDate) {
      newSearch.startDate = startDate.toISODate({ format: 'extended' });
    } else {
      delete newSearch.startDate;
    }

    if (endDate) {
      newSearch.endDate = endDate.toISODate({ format: 'extended' });
    } else {
      delete newSearch.endDate;
    }

    navigate({ search: newSearch });
  };

  const handlePersonsChange = (people: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (people.length === 0) {
      delete newSearch.people;
    } else {
      newSearch.people = people;
    }

    navigate({ search: newSearch });
  };

  const handleProjectsChange = (projects: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (projects.length === 0) {
      delete newSearch.projects;
    } else {
      newSearch.projects = projects;
    }

    navigate({ search: newSearch });
  };

  const handleCategoriesChange = (categories: string[]) => {
    // Reset to page 1 when filters change
    const newSearch = omit(searchParameters, 'page');

    if (categories.length === 0) {
      delete newSearch.categories;
    } else {
      newSearch.categories = categories;
    }

    navigate({ search: newSearch });
  };

  const handleClearSearch = () => {
    // Reset to page 1 when clearing search
    navigate({ search: omit(searchParameters, ['search', 'page']) });
  };

  const handleCreateTimeRecordDialog = () => {
    createTimeRecordDialog.open();
    navigate({ search: { ...searchParameters, add: true } });
  };

  const handleChangeViewMode = (newViewMode: TrackingViewMode) => {
    const newSearch = { ...searchParameters };

    // Clean up default view mode from URL
    if (newViewMode === 'table') {
      delete newSearch.view;
    } else {
      newSearch.view = newViewMode;
    }

    // Reset to page 1 when view mode changes
    delete newSearch.page;

    navigate({ search: newSearch });
  };

  const filterBarProps = {
    search: searchParameters.search,
    startDate: searchParameters.startDate
      ? DateTime.fromISO(searchParameters.startDate)
      : null,
    endDate: searchParameters.endDate
      ? DateTime.fromISO(searchParameters.endDate)
      : null,
    personIds: searchParameters.people ?? [],
    projectIds: searchParameters.projects ?? [],
    categories: searchParameters.categories ?? [],
  };

  return (
    <Container maxWidth="xl">
      <TimeRecordDialog
        isOpen={createTimeRecordDialog.isOpen}
        onClose={() => {
          createTimeRecordDialog.close();
          if (searchParameters.add) {
            navigate({ search: { ...searchParameters, add: undefined } });
          }
        }}
        invalidationKeys={[['time-records'], ['time-record-aggregate']]}
      />
      <TimeDataImportDialog
        isOpen={timeDataImportDialog.isOpen}
        onClose={timeDataImportDialog.close}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['time-records'] });
          queryClient.invalidateQueries({
            queryKey: ['time-record-aggregate'],
          });
        }}
      />
      <TimeDataUpdateDialog
        isOpen={timeDataUpdateDialog.isOpen}
        onClose={timeDataUpdateDialog.close}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['time-records'] });
          queryClient.invalidateQueries({
            queryKey: ['time-record-aggregate'],
          });
        }}
      />
      <PageContent>
        <TimeRecordsSummary filter={summaryFilter} />
        <Stack paddingTop={1}>
          {/* View mode toggle and action buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TimeViewModeToggle
              value={viewMode}
              onChange={handleChangeViewMode}
            />
            <ButtonGroup size="small" variant="contained" ref={actionGroupRef}>
              <Button
                startIcon={<AddRounded />}
                onClick={handleCreateTimeRecordDialog}
              >
                Add time
              </Button>
              <Button onClick={actionMenu.open}>
                <ArrowDropDownRounded fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              open={actionMenu.isOpen}
              onClose={actionMenu.close}
              onClick={actionMenu.close}
              anchorEl={actionGroupRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ disablePadding: true }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
                },
              }}
            >
              <ActionMenuItem
                icon={<MultipleStopOutlined color="action" fontSize="small" />}
                label="Import new from file"
                description="Import new time records in bulk from a CSV file"
                onClick={timeDataImportDialog.open}
              />
              <ActionMenuItem
                icon={<SyncOutlined color="action" fontSize="small" />}
                label="Update existing from file"
                description="Update existing time records in bulk from a CSV file"
                onClick={timeDataUpdateDialog.open}
              />
            </Menu>
          </Stack>

          {/* Filter bar */}
          <TimeRecordFilterBar
            filter={filterBarProps}
            onSearchChange={handleSearchChange}
            onDateRangeChange={handleDateRangeChange}
            onPersonsChange={handlePersonsChange}
            onProjectsChange={handleProjectsChange}
            onCategoriesChange={handleCategoriesChange}
            onClearSearch={handleClearSearch}
          />

          {/* Time record table and analytics view */}
          <SupportErrorBoundary message="An error occurred while loading time records.">
            {viewMode === 'table' && (
              <Suspense fallback={<TimeRecordTableSkeleton />}>
                <TimeRecordTableView
                  sort={sort}
                  filter={timeRecordFilter}
                  onSort={handleSort}
                  onPageChange={handleChangePage}
                />
              </Suspense>
            )}

            {viewMode === 'analytics' && (
              <TimeRecordAnalytics filter={summaryFilter} />
            )}
          </SupportErrorBoundary>
        </Stack>
      </PageContent>
    </Container>
  );
}
